import {createSlice} from '@reduxjs/toolkit';

import MojitoCore from 'mojito/core';

const reduxInstance = MojitoCore.Services.redux;

export const STORE_KEY = 'MozaicStore';

const SESSION_STORAGE_SELECTED_CONFIG_ROOT = 'dbx.mozaic.current-config-root';
const SESSION_STORAGE_SELECTED_PALETTE_ROOT = 'dbx.mozaic.current-palette-root';
const SESSION_STORAGE_SELECTED_SPRITES_ROOT = 'dbx.mozaic.current-sprites-root';
const SESSION_STORAGE_SELECTED_TRANSLATIONS_ROOT = 'dbx.mozaic.current-translations-root';
const SESSION_STORAGE_SELECTED_TOOLS_ITEM = 'dbx.mozaic.current-tools-item';
const SESSION_STORAGE_SELECTED_SEO_ITEM = 'dbx.mozaic.current-seo-item';

export const INITIAL_STATE = {
    userRole: null,
    isVisible: false,
    isCMS: false,
    currentConfigRootPath: sessionStorage.getItem(SESSION_STORAGE_SELECTED_CONFIG_ROOT) || 'debug',
    currentPaletteRootPath: sessionStorage.getItem(SESSION_STORAGE_SELECTED_PALETTE_ROOT) || 'appearance.BASE_PALETTE',
    currentTranslationsRootPath:
        sessionStorage.getItem(SESSION_STORAGE_SELECTED_TRANSLATIONS_ROOT) || 'DOCUMENT_HEAD.PAGE_TITLE',
    currentSpritesRootPath: sessionStorage.getItem(SESSION_STORAGE_SELECTED_SPRITES_ROOT) || 'sport',
    currentToolsItem: sessionStorage.getItem(SESSION_STORAGE_SELECTED_TOOLS_ITEM) || 'genbet-translations',
    currentSEOItem: sessionStorage.getItem(SESSION_STORAGE_SELECTED_SEO_ITEM) || 'defaults/home-page',
    currentRootPathHighlightedItemId: null,
    isDataSending: false,
    isCtrlKeyPressed: false,
};

export const selectState = state => {
    const appState = state || reduxInstance.store?.getState();

    return appState?.[STORE_KEY] || INITIAL_STATE;
};

export const selectUserRole = state => selectState(state).userRole;
export const selectVisibility = state => selectState(state).isVisible;

export const selectIsCMS = state => selectState(state).isCMS;

export const selectCurrentConfigRootPath = state => selectState(state).currentConfigRootPath;
export const selectCurrentPaletteRootPath = state => selectState(state).currentPaletteRootPath;
export const selectCurrentSpritesRootPath = state => selectState(state).currentSpritesRootPath;
export const selectCurrentTranslationsRootPath = state => selectState(state).currentTranslationsRootPath;
export const selectCurrentToolsItem = state => selectState(state).currentToolsItem;
export const selectCurrentSEOItem = state => selectState(state).currentSEOItem;
export const selectCurrentRootPathHighlightedItemId = state => selectState(state).currentRootPathHighlightedItemId;

export const selectIsDataSending = state => selectState(state).isDataSending;
export const selectCtrlKeyPressedStatus = state => selectState(state).isCtrlKeyPressed;

export const {reducer, actions: uiActions} = createSlice({
    name: 'mozaic',
    initialState: INITIAL_STATE,
    reducers: {
        updateUserRole(state, {payload: userRole}) {
            state.userRole = userRole;
        },
        updateIsVisible(state, {payload: isVisible}) {
            state.isVisible = isVisible;
        },
        isCMS(state, {payload: isCMS}) {
            state.isCMS = isCMS;
        },
        updateCurrentConfigRootPath(state, {payload: {currentRootPath, itemIdToHighlight}}) {
            state.currentConfigRootPath = currentRootPath;
            state.currentRootPathHighlightedItemId = itemIdToHighlight;
            sessionStorage.setItem(SESSION_STORAGE_SELECTED_CONFIG_ROOT, currentRootPath);
        },
        updateCurrentPaletteRootPath(state, {payload: {currentRootPath, itemIdToHighlight}}) {
            state.currentPaletteRootPath = currentRootPath;
            state.currentRootPathHighlightedItemId = itemIdToHighlight;
            sessionStorage.setItem(SESSION_STORAGE_SELECTED_PALETTE_ROOT, currentRootPath);
        },
        updateCurrentSpritesRootPath(state, {payload: {currentRootPath, itemIdToHighlight}}) {
            state.currentSpritesRootPath = currentRootPath;
            state.currentRootPathHighlightedItemId = itemIdToHighlight;
            sessionStorage.setItem(SESSION_STORAGE_SELECTED_SPRITES_ROOT, currentRootPath);
        },
        updateCurrentRootPathHighlightedItemId(state, {payload: itemIdToHighlight}) {
            state.currentRootPathHighlightedItemId = itemIdToHighlight;
        },
        updateCurrentTranslationsRootPath(state, {payload: currentTranslationsRootPath}) {
            state.currentTranslationsRootPath = currentTranslationsRootPath;
            sessionStorage.setItem(SESSION_STORAGE_SELECTED_TRANSLATIONS_ROOT, currentTranslationsRootPath);
        },
        updateCurrentToolsItem(state, {payload: currentToolsItem}) {
            state.currentToolsItem = currentToolsItem;
            sessionStorage.setItem(SESSION_STORAGE_SELECTED_TOOLS_ITEM, currentToolsItem);
        },
        updateCurrentSEOItem(state, {payload: currentSEOItem}) {
            state.currentSEOItem = currentSEOItem;
            sessionStorage.setItem(SESSION_STORAGE_SELECTED_SEO_ITEM, currentSEOItem);
        },
        updateDataSending(state, {payload}) {
            state.isDataSending = payload;
        },
        updateCtrlKeyPressedStatus(state, {payload}) {
            state.isCtrlKeyPressed = payload;
        },
    },
});

reduxInstance.injectReducer(STORE_KEY, reducer);
